import { useEffect, useState } from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import type { AiSuggestionsPlugin, AiSuggestionsSharedState } from '../../aiSuggestionsPluginType';
import type { ThinkingStatus } from '../../pm-plugins/thinking-plugin';

const selectThinkingStatus = (
	state: AiSuggestionsSharedState | undefined,
): ThinkingStatus | undefined => state?.thinkingStatus;

/**
 * Subscribes synchronously (no debounce) to the `aiSuggestions` plugin sharedState
 * and returns `thinkingStatus`.
 */
export const useSyncedThinkingStatus = (
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>,
): ThinkingStatus | undefined => {
	const [thinkingStatus, setThinkingStatus] = useState<ThinkingStatus | undefined>(() =>
		selectThinkingStatus(api?.aiSuggestions?.sharedState.currentState()),
	);

	useEffect(() => {
		const aiSuggestions = api?.aiSuggestions;
		if (!aiSuggestions) {
			return;
		}

		// Re-read once at subscription time so we don't miss a transaction that fired between
		// the initial render and the effect commit.
		setThinkingStatus(selectThinkingStatus(aiSuggestions.sharedState.currentState()));

		return aiSuggestions.sharedState.onChange(({ nextSharedState }) => {
			setThinkingStatus(selectThinkingStatus(nextSharedState));
		});
	}, [api]);

	return thinkingStatus;
};
